import { api } from './_client';
import type {
  MaintenanceRecord,
  MaintenanceRecordCreate,
  MaintenanceTypesResponse,
} from '../../types/api';

export const maintenanceApi = {
  // Get maintenance types
  async getMaintenanceTypes(): Promise<MaintenanceTypesResponse> {
    const response = await api.get<MaintenanceTypesResponse>('/maintenance/types');
    return response.data;
  },

  // Create a new maintenance type
  async createMaintenanceType(
    label: string
  ): Promise<{ id: string; value: string; label: string }> {
    const response = await api.post<{ id: string; value: string; label: string }>(
      '/maintenance/types',
      { label }
    );
    return response.data;
  },

  // Create maintenance records (returns array, one per selected type)
  async createMaintenanceRecord(record: MaintenanceRecordCreate): Promise<MaintenanceRecord[]> {
    const response = await api.post<MaintenanceRecord[]>('/maintenance/records', record);
    return response.data;
  },

  // Get maintenance records
  async getMaintenanceRecords(droneSerial?: string, limit?: number): Promise<MaintenanceRecord[]> {
    const params = new URLSearchParams();
    if (droneSerial) params.append('drone_serial', droneSerial);
    if (limit) params.append('limit', limit.toString());

    const url = `/maintenance/records${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await api.get<MaintenanceRecord[]>(url);
    return response.data;
  },

  // Get maintenance records for specific drone
  async getDroneMaintenanceRecords(
    droneSerial: string,
    limit?: number
  ): Promise<MaintenanceRecord[]> {
    const params = limit ? `?limit=${limit}` : '';
    const response = await api.get<MaintenanceRecord[]>(
      `/maintenance/records/${droneSerial}${params}`
    );
    return response.data;
  },

  // Update maintenance record
  async updateMaintenanceRecord(
    recordId: string,
    updates: Partial<MaintenanceRecordCreate>
  ): Promise<MaintenanceRecord> {
    const response = await api.put<MaintenanceRecord>(`/maintenance/records/${recordId}`, updates);
    return response.data;
  },

  // Delete maintenance record
  async deleteMaintenanceRecord(recordId: string): Promise<{ success: boolean }> {
    const response = await api.delete<{ success: boolean }>(`/maintenance/records/${recordId}`);
    return response.data;
  },

  // Update maintenance record status
  async updateMaintenanceStatus(recordId: string, status: string): Promise<MaintenanceRecord> {
    const params = new URLSearchParams({ status });
    const response = await api.patch<MaintenanceRecord>(
      `/maintenance/records/${recordId}/status?${params.toString()}`
    );
    return response.data;
  },

  // Update maintenance record date
  async updateMaintenanceDate(
    recordId: string,
    startDate: string,
    endDate: string
  ): Promise<MaintenanceRecord> {
    const params = new URLSearchParams({ start_date: startDate, end_date: endDate });
    const response = await api.patch<MaintenanceRecord>(
      `/maintenance/records/${recordId}/date?${params.toString()}`
    );
    return response.data;
  },

  // Update maintenance record content
  async updateMaintenanceContent(
    recordId: string,
    workDescription: string
  ): Promise<MaintenanceRecord> {
    const params = new URLSearchParams({ work_description: workDescription });
    const response = await api.patch<MaintenanceRecord>(
      `/maintenance/records/${recordId}/content?${params.toString()}`
    );
    return response.data;
  },

  // Update maintenance record quality check status
  async updateMaintenanceQualityCheck(
    recordId: string,
    qualityChecked: boolean
  ): Promise<MaintenanceRecord> {
    const params = new URLSearchParams({ quality_checked: qualityChecked.toString() });
    const response = await api.patch<MaintenanceRecord>(
      `/maintenance/records/${recordId}/quality-check?${params.toString()}`
    );
    return response.data;
  },

  // Get maintenance history for a drone
  async getMaintenanceHistory(droneSerial: string): Promise<MaintenanceRecord[]> {
    const response = await api.get<MaintenanceRecord[]>(`/maintenance-history/${droneSerial}`);
    return response.data;
  },

  // Get complete maintenance timeline for a drone (past and future records)
  async getMaintenanceTimeline(droneSerial: string): Promise<
    Array<{
      id: string;
      start_date: string;
      end_date: string;
      maintenance_type: string;
      performed_by_name?: string;
    }>
  > {
    const response = await api.get(`/maintenance-timeline/${droneSerial}`);
    return response.data;
  },

  // Recalculate maintenance counters for a drone
  async recalculateMaintenanceCounters(droneSerial: string): Promise<{
    success: boolean;
    drone_serial: string;
    timestamp: string;
    summary: {
      total_flight_hours: number;
      total_engine_hours: number;
      maintenance_records_processed: number;
      counters_reset: number;
      counters_unchanged: number;
    };
    counters_reset: Array<{
      counter_key: string;
      counter_name: string;
      category: string;
      hour_type: string;
      previous_value: number;
      new_value: number;
      changed: boolean;
      reset_by: {
        maintenance_type: string;
        date: string;
        performed_by: string;
      } | null;
      flights_since_maintenance: Array<{
        date: string;
        flight_hours: number | null;
        engine_hours: number | null;
      }>;
      interval: number;
      hours_remaining: number | null;
      status: string;
    }>;
    counters_unchanged: Array<{
      counter_key: string;
      counter_name: string;
      category: string;
      hour_type: string;
      previous_value: number;
      new_value: number;
      changed: boolean;
      reset_by: {
        maintenance_type: string;
        date: string;
        performed_by: string;
      } | null;
      flights_since_maintenance: Array<{
        date: string;
        flight_hours: number | null;
        engine_hours: number | null;
      }>;
      interval: number;
      hours_remaining: number | null;
      status: string;
    }>;
  }> {
    const response = await api.post(`/maintenance/recalculate/${droneSerial}`);
    return response.data;
  },
};
